(function () {
  const SELECTOR = '.marquee-container';

  const debounce = (fn, ms = 150) => {
    let t;
    return (...a) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...a), ms);
    };
  };

  function readSettings(el) {
    const s = el.dataset || {};
    const isMobile = window.matchMedia('(max-width: 767px)').matches;

    const base = {
      // duration (ms) to move by one full set width
      speed: Number(s.speed) || 20000,
      gap: Number(s.gap) || 15,
      duplicateCount: Number(s.duplicateCount) || 2,
      pauseOnHover: s.pauseOnHover !== 'false',
      smoothEdges: s.smoothEdges === 'true',
      // If you’ve removed direction from the schema, this will fall back to left.
      direction: (s.direction || 'left').toLowerCase()
    };

    if (isMobile && s.mobileSpeed) {
      base.speed = Number(s.mobileSpeed) || base.speed;
    }
    return base;
  }

  // -------- Preferred: vendor ----------
  function buildVendor(el) {
    if (!window.InfiniteMarquee) return false;
    if (el._marqueeDestroy) { el._marqueeDestroy(); el._marqueeDestroy = null; }

    const opts = readSettings(el);
    const im = new window.InfiniteMarquee({
      element: el,
      speed: opts.speed,
      smoothEdges: opts.smoothEdges,
      pauseOnHover: opts.pauseOnHover,
      // If direction data-attr exists the lib will use it; otherwise it uses its default.
      direction: opts.direction,
      gap: `${opts.gap}px`,
      duplicateCount: opts.duplicateCount,
      mobileSettings: {
        speed: opts.speed
      }
    });

    el._marqueeDestroy = () => im.destroy();
    return true;
  }

  // -------- Seamless fallback ----------
  function buildFallback(el) {
    if (el._marqueeDestroy) { el._marqueeDestroy(); el._marqueeDestroy = null; }
    const opts = readSettings(el);

    // collect originals before we clear the container
    const originalItems = Array.from(el.querySelectorAll('.marquee-item'));
    if (!originalItems.length) return;

    el.style.position = 'relative';
    el.style.overflow = 'hidden';
    el.classList.add('marquee--fallback');

    // fresh track
    const track = document.createElement('div');
    track.className = 'marquee-track';
    Object.assign(track.style, {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
      willChange: 'transform',
      transform: 'translateX(0)'
    });

    // helper: build one "set" (a single sequence of all items)
    const makeSet = () => {
      const set = document.createElement('div');
      set.className = 'marquee-set';
      // Important: do NOT also set CSS gap here, items already have margin-right via Liquid.
      Object.assign(set.style, {
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap'
      });
      originalItems.forEach(node => {
        set.appendChild(node.cloneNode(true)); // margins come from inline styles in your Liquid
      });
      return set;
    };

    const firstSet = makeSet();
    track.appendChild(firstSet);
    el.innerHTML = '';
    el.appendChild(track);

    const initAnim = () => {
      const containerWidth = el.clientWidth;

      // Measure width of ONE set after all images have loaded
      const setWidth = firstSet.scrollWidth;

      // Ensure track is wide enough so there’s never blank space while we loop by setWidth.
      // Rule of thumb: totalWidth >= containerWidth + setWidth
      while (track.scrollWidth < containerWidth + setWidth) {
        track.appendChild(makeSet());
      }

      let pos = 0;
      let running = true;
      let lastTs = performance.now();

      // pixels per millisecond to move exactly one set width in `speed` ms
      const pxPerMs = setWidth / opts.speed;
      const dir = opts.direction === 'right' ? 1 : -1;

      function step(ts) {
        if (!running) { lastTs = ts; requestAnimationFrame(step); return; }
        const dt = ts - lastTs;
        lastTs = ts;

        // move
        pos += dir * pxPerMs * dt;

        // wrap precisely by one set width (no half/trackWidth guesses)
        if (dir < 0 && pos <= -setWidth) pos += setWidth;
        if (dir > 0 && pos >= 0)        pos -= setWidth;

        track.style.transform = `translateX(${pos}px)`;
        requestAnimationFrame(step);
      }
      requestAnimationFrame(step);

      if (opts.pauseOnHover) {
        el.addEventListener('mouseenter', () => { running = false; });
        el.addEventListener('mouseleave', () => { running = true; lastTs = performance.now(); });
      }

      el._marqueeDestroy = () => {
        el.innerHTML = '';
        // restore original nodes
        const restoreTrack = document.createElement('div');
        restoreTrack.className = 'marquee-track';
        originalItems.forEach(n => restoreTrack.appendChild(n));
        el.appendChild(restoreTrack);
        el.classList.remove('marquee--fallback');
      };
    };

    // wait for all images so width is correct
    const imgs = Array.from(el.querySelectorAll('img'));
    if (imgs.length) {
      let pending = imgs.length;
      const done = () => { if (--pending <= 0) initAnim(); };
      imgs.forEach(img => {
        if (img.complete) done();
        else { img.addEventListener('load', done); img.addEventListener('error', done); }
      });
    } else {
      initAnim();
    }
  }

  // -------- lifecycle ----------
  function initOne(el) {
    if (el.dataset.rsMarqueeInit === 'true') return;
    const usedVendor = !!buildVendor(el);
    if (!usedVendor) buildFallback(el);
    el.dataset.rsMarqueeInit = 'true';
  }

  function reinitOne(el) {
    if (el._marqueeDestroy) el._marqueeDestroy();
    el.dataset.rsMarqueeInit = 'false';
    initOne(el);
  }

  function initScope(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScope(document));
  } else {
    initScope(document);
  }

  document.addEventListener('shopify:section:load', (e) => initScope(e.target));
  document.addEventListener('shopify:section:select', (e) => initScope(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll(SELECTOR).forEach((el) => {
      if (el._marqueeDestroy) el._marqueeDestroy();
      el.dataset.rsMarqueeInit = 'false';
    });
  });

  const onResize = debounce(() => {
    document.querySelectorAll(SELECTOR).forEach(reinitOne);
  }, 200);
  window.addEventListener('resize', onResize);
})();
